/**
 * Host DOM integration helpers — called from inside the Shadow DOM Web
 * Component mounted on the merchant's own origin. They read / click the
 * merchant's size buttons, with careful OOS gating.
 *
 * Pure functions, no React. Both are exported from the barrel so legacy
 * callers (`import { performHostAutoSelect, checkHostSizeStock } from
 * ".../WizardPreview"`) keep working.
 */

/**
 * Attempt to auto-select the recommended size on the merchant's live page.
 *
 * This runs INSIDE a Shadow DOM Web Component mounted on the merchant's
 * origin, so `window.document` IS the host page. We locate the merchant's
 * size buttons via a merchant-provided CSS selector, read the size value
 * from a configurable attribute (fallback: textContent), and gate the
 * click on an "out of stock" class presence (present = unavailable).
 * Matches real-world e-commerce patterns (Shopify, Woo, Ticimax, Trendyol)
 * where sold-out variants carry an extra class like `disabled` / `sold-out`.
 *
 * The merchant is responsible for keeping the sizes they stock aligned
 * with the sizes defined in their Beden Tablosu — no mapping layer here.
 *
 * Returns one of:
 *   { ok: true,          size, siteSize }         — clicked successfully
 *   { ok: false, code: "disabled" }                — integration toggle OFF
 *   { ok: false, code: "no-recommendation" }       — recommendation.size missing
 *   { ok: false, code: "no-elements", siteSize }   — selector found 0 matches
 *   { ok: false, code: "size-not-found", siteSize, available }
 *   { ok: false, code: "out-of-stock", siteSize }  — found but every match carries the OOS class
 *   { ok: false, code: "error", error }            — threw
 */
export const performHostAutoSelect = (recommendedSize, siteIntegration) => {
    try {
        if (!siteIntegration || siteIntegration.enabled !== true) {
            return { ok: false, code: "disabled" };
        }
        if (!recommendedSize) {
            return { ok: false, code: "no-recommendation" };
        }
        if (typeof window === "undefined" || !window.document) {
            return { ok: false, code: "no-dom" };
        }

        const selector = (siteIntegration.sizeSelector || "").trim();
        if (!selector) return { ok: false, code: "no-selector" };

        const nodes = Array.from(window.document.querySelectorAll(selector));
        if (!nodes.length) {
            return { ok: false, code: "no-elements" };
        }

        const siteSize = String(recommendedSize);
        const targetNorm = siteSize.trim().toUpperCase();

        const attr = (siteIntegration.sizeAttribute || "").trim();
        const readSize = (el) => {
            if (attr) {
                const v = el.getAttribute(attr);
                if (v != null) return String(v).trim();
            }
            return String(el.textContent || "").trim();
        };

        // Collect every element whose value matches the target (case-insensitive).
        const matches = nodes.filter((el) => readSize(el).toUpperCase() === targetNorm);
        if (!matches.length) {
            const available = nodes.map((el) => readSize(el)).filter(Boolean);
            return { ok: false, code: "size-not-found", siteSize, available };
        }

        // Out-of-stock gating. Merchant can configure ANY CSS selector that
        // matches an unavailable button — class (`.sold-out`), attribute
        // (`[data-stock="0"]`), or combined (`button.disabled[aria-disabled="true"]`).
        // We use `el.matches(selector)` so both class and attribute forms
        // work from the same single field. If merchant left the selector
        // empty, assume every rendered size is in stock. Otherwise, matches
        // that satisfy the selector are skipped; we pick the first one that
        // does NOT match it.
        const oosSel = (siteIntegration.outOfStockSelector || "").trim();
        let target = null;
        if (!oosSel) {
            target = matches[0];
        } else {
            try {
                target = matches.find((el) => !el.matches(oosSel));
            } catch (_) {
                // Invalid selector — fall back to treating all matches as
                // in stock so the merchant's auto-select still works. The
                // bad selector is a config problem; we surface it as a
                // "no OOS detection" soft-fail rather than a hard crash.
                target = matches[0];
            }
        }

        if (!target) {
            return { ok: false, code: "out-of-stock", siteSize };
        }

        // A native `.click()` call dispatches a trusted click event that
        // triggers React/Vue onClick handlers, native button handlers and
        // delegated listeners alike — no extra synthetic input/change
        // dispatch needed for `<button>` elements.
        target.click();

        // Scroll the clicked button into view so the visitor sees the merchant
        // UI react — otherwise the modal just closes and the state change
        // happens off-screen.
        try {
            target.scrollIntoView({ behavior: "smooth", block: "center" });
        } catch (_) { /* ignore */ }

        return { ok: true, size: recommendedSize, siteSize };
    } catch (error) {
        return { ok: false, code: "error", error: (error && error.message) || String(error) };
    }
};

/**
 * Lightweight host-DOM stock probe for the Result / Welcome Recommend
 * screens. Runs on every render inside the merchant's Shadow DOM Web
 * Component, so it reflects live DOM state (e.g. a merchant SPA just
 * toggled a size's stock class). Shares all parsing logic with
 * `performHostAutoSelect` but ONLY observes — never clicks, never scrolls.
 *
 * Returns:
 *   { status: "disabled" }     — integration toggle off, or no size given
 *   { status: "not-found" }    — selector mounted 0 nodes OR no size match
 *   { status: "in-stock" }     — at least one size button matches AND is
 *                                not flagged by the OOS selector
 *   { status: "out-of-stock" } — every matching button is flagged OOS
 *
 * This drives the subtle in-stock / out-of-stock confidence cues on the
 * recommendation screens (see ResultStep + WelcomeStep render paths). The
 * "not-found" case is treated as "stock unknown" by the UI so we don't
 * misrepresent the merchant's catalog when their selector is misconfigured.
 */
export const checkHostSizeStock = (size, siteIntegration) => {
    if (!siteIntegration || siteIntegration.enabled !== true) return { status: "disabled" };
    if (!size) return { status: "disabled" };
    if (typeof window === "undefined" || !window.document) return { status: "disabled" };

    const selector = (siteIntegration.sizeSelector || "").trim();
    if (!selector) return { status: "disabled" };

    let nodes;
    try {
        nodes = Array.from(window.document.querySelectorAll(selector));
    } catch (_) {
        return { status: "not-found" };
    }
    if (!nodes.length) return { status: "not-found" };

    const siteSize = String(size);
    const targetNorm = siteSize.trim().toUpperCase();
    const attr = (siteIntegration.sizeAttribute || "").trim();
    const readSize = (el) => {
        if (attr) {
            const v = el.getAttribute(attr);
            if (v != null) return String(v).trim();
        }
        return String(el.textContent || "").trim();
    };

    const matches = nodes.filter((el) => readSize(el).toUpperCase() === targetNorm);
    if (!matches.length) return { status: "not-found" };

    const oosSel = (siteIntegration.outOfStockSelector || "").trim();
    if (!oosSel) return { status: "in-stock" };

    try {
        const anyInStock = matches.some((el) => !el.matches(oosSel));
        return { status: anyInStock ? "in-stock" : "out-of-stock" };
    } catch (_) {
        // Bad selector — treat as "we can't tell" to avoid a false red flag
        return { status: "not-found" };
    }
};
